import { useState } from 'react'

import { PageHeader } from '../../app/PageHeader'
import analyticsJson from '../../demo/fixtures/analytics.json'
import campaignsJson from '../../demo/fixtures/campaigns.json'
import copyJson from '../../demo/fixtures/copy.json'
import { useAnalyticsView } from '../../demo/selectors'
import type { AnalyticsLens, AnalyticsRangeKey } from '../../demo/types'
import { Icon } from '../../ui'
import { Controls } from './Controls'
import { KpiRow } from './KpiRow'
import { PeriodSummary } from './PeriodSummary'
import { ProductTable } from './ProductTable'
import { TaskTable } from './TaskTable'
import { TrendChart } from './TrendChart'
import { ViewSwitch } from './ViewSwitch'
import styles from './index.module.css'

interface AnalyticsFixture {
  defaultRange: AnalyticsRangeKey
  defaultLens: AnalyticsLens
  compareEnabled: boolean
}

interface CampaignFixture {
  meta: { footnoteAnalytics: string }
}

const analyticsFixture = analyticsJson as unknown as AnalyticsFixture
const campaignFixture = campaignsJson as unknown as CampaignFixture

export function AnalyticsPage() {
  const [range, setRange] = useState<AnalyticsRangeKey>(analyticsFixture.defaultRange)
  const [lens, setLens] = useState<AnalyticsLens>(analyticsFixture.defaultLens)
  const [compare, setCompare] = useState(analyticsFixture.compareEnabled)
  const [tableView, setTableView] = useState<'campaign' | 'product'>('campaign')
  const [highlightedMarker, setHighlightedMarker] = useState<number | null>(null)
  const view = useAnalyticsView(range, lens, compare)

  return (
    <>
      <PageHeader
        title={copyJson.pages.analytics.title}
        subtitle={copyJson.pages.analytics.subtitle}
        action={(
          <Controls
            range={range}
            lens={lens}
            compare={compare}
            onRangeChange={setRange}
            onLensChange={setLens}
            onCompareChange={setCompare}
          />
        )}
      />
      <KpiRow kpis={view.kpis} compare={compare} />
      <div className={styles.insightRow}>
        <TrendChart
          chart={view.chart}
          days={view.days}
          compare={compare}
          highlightedMarker={highlightedMarker}
          onMarkerHover={setHighlightedMarker}
        />
        <PeriodSummary
          items={view.summary}
          highlightedMarker={highlightedMarker}
          onItemHover={setHighlightedMarker}
        />
      </div>
      {tableView === 'campaign' ? (
        <TaskTable
          rows={view.table}
          lens={lens}
          action={<ViewSwitch value={tableView} onChange={setTableView} />}
        />
      ) : (
        <ProductTable
          key={`${range}:${lens}:${String(compare)}`}
          range={range}
          lens={lens}
          action={<ViewSwitch value={tableView} onChange={setTableView} />}
        />
      )}
      <p className={styles.footnote} data-scope="analytics-footnote">
        <Icon name="info" size={16} />
        {campaignFixture.meta.footnoteAnalytics}
      </p>
    </>
  )
}
